<template>
  <div>
    <div class="d-flex align-center mb-2 text-caption">
      <span class="mr-2">
        {{ rows.length }} 名で分割 · 均等割
      </span>
      <v-spacer />
      <v-btn
        text
        x-small
        :ripple="false"
        :disabled="!realTotal"
        @click="resetToEqual"
      >
        <v-icon
          left
          x-small
        >
          {{ icons.mdiRefresh }}
        </v-icon>
        均等割で再計算
      </v-btn>
    </div>

    <v-card
      v-for="(row, idx) in rows"
      :key="idx"
      outlined
      class="mb-2"
    >
      <v-card-text class="pa-3">
        <div class="d-flex align-center">
          <v-chip
            x-small
            label
            class="mr-2"
          >
            {{ idx + 1 }}
          </v-chip>
          <span class="text-body-2 grey--text text--lighten-1">
            {{ row.to || '宛名未入力' }}
          </span>
          <v-spacer />
          <span class="text-body-1 font-weight-bold">
            ¥{{ Number(row.amount).toLocaleString() }}
          </span>
        </div>
        <div class="text-caption grey--text mt-1">
          {{ row.note || '但し書きなし' }}
        </div>
        <div class="d-flex mt-2 align-center">
          <v-spacer />
          <v-btn
            small
            text
            :ripple="false"
            :disabled="!slipBaseOptions"
            @click="openPreview(idx)"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiPrinterEye }}
            </v-icon>
            プレビュー
          </v-btn>
          <v-btn
            v-if="printRowFn"
            small
            text
            color="secondary"
            :ripple="false"
            :disabled="!isReceiptPrinterConnected"
            @click="printRow(row)"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiPrinterPos }}
            </v-icon>
            印刷
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <div class="d-flex align-center text-caption grey--text mt-2">
      <span>小計</span>
      <v-spacer />
      <span>¥{{ subtotal.toLocaleString() }}</span>
    </div>
    <div class="d-flex align-center text-caption grey--text">
      <span>税サ等</span>
      <v-spacer />
      <span>¥{{ serviceFee.toLocaleString() }}</span>
    </div>
    <div
      v-if="processingFee > 0"
      class="d-flex align-center text-caption grey--text"
    >
      <span>会計手数料</span>
      <v-spacer />
      <span>¥{{ processingFee.toLocaleString() }}</span>
    </div>
    <div
      v-if="discountFee > 0"
      class="d-flex align-center text-caption grey--text"
    >
      <span>値引き</span>
      <v-spacer />
      <span>-¥{{ discountFee.toLocaleString() }}</span>
    </div>
    <div class="d-flex align-center text-caption mt-1">
      <span>伝票合計</span>
      <v-spacer />
      <span>¥{{ realTotal.toLocaleString() }}</span>
    </div>
    <div class="d-flex align-center text-caption">
      <span>配分済</span>
      <v-spacer />
      <span>¥{{ allocatedTotal.toLocaleString() }}</span>
    </div>
    <div
      class="d-flex align-center text-caption"
      :class="{ 'red--text': remaining < 0, 'orange--text': remaining > 0, 'success--text': remaining === 0 }"
    >
      <span>残額</span>
      <v-spacer />
      <span>¥{{ remaining.toLocaleString() }}</span>
    </div>

    <v-btn
      block
      outlined
      class="mt-2"
      :ripple="false"
      @click="openEdit(0)"
    >
      <v-icon
        left
        small
      >
        {{ icons.mdiPencilBoxOutline }}
      </v-icon>
      分割を編集
    </v-btn>

    <v-dialog
      v-model="isPreviewOpen"
      max-width="480"
      scrollable
    >
      <v-card>
        <v-card-title>
          プレビュー
          <span class="text-body-2 grey--text ml-2">
            #{{ previewIndex + 1 }} {{ previewRow && previewRow.to || '宛名未入力' }}
          </span>
          <v-spacer />
          <v-btn
            icon
            :ripple="false"
            @click="isPreviewOpen = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <split-row-preview
            v-if="previewRow && slipBaseOptions"
            :base-options="slipBaseOptions"
            :row="previewRow"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <split-receipt-dialog
      v-model="isDialogOpen"
      :rows="rows"
      :real-total="realTotal"
      :subtotal="subtotal"
      :service-fee="serviceFee"
      :processing-fee="processingFee"
      :discount-fee="discountFee"
      :receipt-note-presets="receiptNotePresets"
      :slip-base-options="slipBaseOptions"
      :initial-focus-index="editingIndex"
      @confirm="onDialogConfirm"
    />
  </div>
</template>

<script>
import {
  ref, computed, watch, inject,
} from '@vue/composition-api'
import {
  mdiPencilBoxOutline,
  mdiRefresh,
  mdiPrinterEye,
  mdiPrinterPos,
  mdiClose,
} from '@mdi/js'
import {
  equalSplit, remainingAmount,
} from '@/views/composable/useReceiptSplit'
import SplitReceiptDialog from './SplitReceiptDialog.vue'
import SplitRowPreview from './SplitRowPreview.vue'

export default {
  components: { SplitReceiptDialog, SplitRowPreview },
  props: {
    value: { type: Array, default: () => [] },
    realTotal: { type: Number, required: true },
    subtotal: { type: Number, default: 0 },
    serviceFee: { type: Number, default: 0 },
    processingFee: { type: Number, default: 0 },
    discountFee: { type: Number, default: 0 },
    customerCount: { type: Number, default: 1 },
    // 預かり金 (paymentsAttributes 等) — 複数 payments があるとき行のデフォルトに使う
    payments: { type: Array, default: () => [] },
    // 顧客一覧 — 各行の宛名 (receiptAddressee) のデフォルトに使う
    customers: { type: Array, default: () => [] },
    receiptNotePresets: { type: Array, default: () => [] },
    // ハードウェア印刷オプションのベース (印字面プレビュー用に使う)
    slipBaseOptions: { type: [Object, null], default: null },
    // 行ごとの再印刷関数 (会計後の再印刷で使う)。
    // 渡されたとき各行に「印刷」ボタンが出る。
    printRowFn: { type: Function, default: null },
  },
  setup(props, { emit }) {
    const isReceiptPrinterConnected = inject('isReceiptPrinterConnected', false)
    const printRow = row => {
      if (props.printRowFn) props.printRowFn(row)
    }
    const isDialogOpen = ref(false)
    const editingIndex = ref(0)
    const isPreviewOpen = ref(false)
    const previewIndex = ref(0)

    const defaultNote = computed(() => {
      const first = props.receiptNotePresets[0]

      return first?.attributes?.content || ''
    })

    // 各行の宛名のデフォルト: 顧客が紐付いていれば customers[i].receiptAddressee を使う
    const defaultToForIndex = idx => (props.customers || [])[idx]?.receiptAddressee || ''

    const makeRows = amounts => amounts.map((amount, idx) => ({
      to: defaultToForIndex(idx),
      amount,
      note: defaultNote.value,
      issuedDate: new Date(),
    }))

    // 預かり金が複数行あるとき (例: 現金 + カード) は、その振り分けをそのまま
    // 行のデフォルトに使う。1件以下なら customerCount で均等割するフォールバック。
    const makeDefaultRows = () => {
      const validPayments = (props.payments || []).filter(p => Number(p.amount || 0) > 0)
      if (validPayments.length > 1) {
        return validPayments.map((p, idx) => ({
          to: defaultToForIndex(idx),
          amount: Number(p.amount || 0),
          note: defaultNote.value,
          issuedDate: new Date(),
        }))
      }

      return makeRows(equalSplit(props.realTotal, props.customerCount))
    }

    const rows = computed({
      get: () => {
        if (props.value.length > 0) return props.value

        return makeDefaultRows()
      },
      set: next => emit('input', next),
    })

    // value が空のまま realTotal が確定するケースで初期値を流し込む
    if (props.value.length === 0) {
      emit('input', makeDefaultRows())
    }

    // 値引きや手数料の変更で realTotal が変わったとき、行 amounts と乖離する。
    // 旧 realTotal と行合計が一致していた (= 整合に組まれていた) ときだけ
    // 均等割で再計算する。ユーザが手動でずらしていたら上書きしない。
    watch(() => props.realTotal, (newTotal, oldTotal) => {
      if (newTotal === oldTotal) return
      const current = props.value
      if (!current || current.length === 0) return
      const sum = current.reduce((acc, r) => acc + Number(r.amount || 0), 0)
      if (sum === oldTotal) emit('input', makeDefaultRows())
    })

    const allocatedTotal = computed(() => rows.value.reduce((acc, r) => acc + Number(r.amount || 0), 0))
    const remaining = computed(() => remainingAmount(props.realTotal, rows.value.map(r => Number(r.amount || 0))))

    // 「均等割で再計算」: 会計手数料込みの伝票合計 (realTotal) を人数で均等割する。
    // realTotal は currentTotal (手数料込み) なので、会計手数料を後から付けても
    // 再計算すれば手数料込みの金額で分割される。
    const resetToEqual = () => {
      const amounts = equalSplit(props.realTotal, rows.value.length)
      const next = amounts.map((amount, idx) => ({
        to: rows.value[idx]?.to || '',
        amount,
        note: rows.value[idx]?.note || defaultNote.value,
        issuedDate: rows.value[idx]?.issuedDate || new Date(),
      }))
      emit('input', next)
    }

    const openEdit = idx => {
      editingIndex.value = idx
      isDialogOpen.value = true
    }

    const previewRow = computed(() => rows.value[previewIndex.value] || null)

    const openPreview = idx => {
      previewIndex.value = idx
      isPreviewOpen.value = true
    }

    const onDialogConfirm = nextRows => {
      emit('input', nextRows)
      isDialogOpen.value = false
    }

    return {
      rows,
      isDialogOpen,
      editingIndex,
      isPreviewOpen,
      previewIndex,
      previewRow,
      allocatedTotal,
      remaining,
      resetToEqual,
      openEdit,
      openPreview,
      onDialogConfirm,
      isReceiptPrinterConnected,
      printRow,
      icons: {
        mdiPencilBoxOutline, mdiRefresh, mdiPrinterEye, mdiPrinterPos, mdiClose,
      },
    }
  },
}
</script>
